/**
 * OrderDocuments — Sipariş belgelerinin kalıcı indirme şeridi.
 *
 * Belgeler yalnızca durum geçişi anında bir kez inmek yerine, numarası
 * üretilmiş her belge (KM/LS/RE) buradan istenildiği an yeniden indirilir.
 * Warenausgang, Versand ve Admin sipariş kartlarında kullanılır.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ClipboardList, FileText, Receipt, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { downloadKommissionierschein } from '@/components/orders/KommissionierscheinPDF';
import { downloadLieferschein } from '@/components/orders/LieferscheinPDF';
import { downloadRechnung } from '@/components/orders/RechnungPDF';

const DOCS = [
  {
    key: 'km',
    numberField: 'picking_number',
    label: 'Kommissionierschein',
    icon: ClipboardList,
    className: 'text-blue-600 border-blue-200 hover:bg-blue-50',
    download: (order, products) => downloadKommissionierschein(order, products),
  },
  {
    key: 'ls',
    numberField: 'delivery_note_number',
    label: 'Lieferschein',
    icon: FileText,
    className: 'text-indigo-600 border-indigo-200 hover:bg-indigo-50',
    download: (order, products) =>
      downloadLieferschein(order, undefined, { exemplar: 'Kunde', products }),
  },
  {
    key: 're',
    numberField: 'invoice_number',
    label: 'Rechnung',
    icon: Receipt,
    className: 'text-emerald-600 border-emerald-200 hover:bg-emerald-50',
    download: (order) => downloadRechnung(order),
  },
];

export default function OrderDocuments({ order, products = [], size = 'sm' }) {
  const [downloading, setDownloading] = useState(null);

  const available = DOCS.filter((doc) => order?.[doc.numberField]);
  if (available.length === 0) return null;

  const handleDownload = async (doc) => {
    setDownloading(doc.key);
    try {
      await doc.download(order, products);
    } catch (err) {
      console.warn(`[OrderDocuments] ${doc.label} PDF failed:`, err);
      toast.error(`${doc.label} konnte nicht erzeugt werden.`);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="order-documents">
      {available.map((doc) => {
        const Icon = doc.icon;
        return (
          <Button
            key={doc.key}
            type="button"
            variant="outline"
            size={size}
            disabled={downloading !== null}
            onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}
            className={`h-9 sm:h-8 gap-1.5 px-3 sm:px-2.5 text-[10px] font-bold uppercase cursor-pointer ${doc.className}`}
            title={`${doc.label} ${order[doc.numberField]} herunterladen`}
          >
            {downloading === doc.key
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <Icon className="w-3 h-3" />}
            {order[doc.numberField]}
          </Button>
        );
      })}
    </div>
  );
}
